import { dayjs, ElTableColumn } from 'element-plus'
import { useI18n } from 'i18n-utils'
import { ContractServices, OrderDirection } from 'network'
import {
  useBaseInfoStore,
  useDoubleContractAssetsAccountStore,
  useExchangeInfoStore,
  useLoadMore,
  useProjectTheme,
  UserDoubleContractUnitType,
  useResponsive,
  useUserCenterContractOrderStore,
} from 'store'
import { StorageIndexDBEnum } from 'store/src/storage/StorageDB'
import { DefineComponentProps } from 'utils'
import { EmptyData, FullScreenMinHeight, HocElTable, LoadMore, Pnl, Tag, VirtualList } from '..'
import { CopyText } from '../components/Copy'
import { OrderListLayout } from '../layouts/Order.layout'
import { HistoricalTradeItem } from './HistoricalTradeItem'

export type HistoricalTradeProps = {
  positionId?: string
}

export const HistoricalTrade = defineComponent<HistoricalTradeProps>({
  props: DefineComponentProps<HistoricalTradeProps>(['positionId']),
  setup(props) {
    const contractOrderListStore = useUserCenterContractOrderStore()
    const baseInfoStore = useBaseInfoStore()
    const exchangeStore = useExchangeInfoStore()
    const { t } = useI18n()
    const themeStore = useProjectTheme()
    const theme = computed(() => themeStore.theme)
    const responsive = useResponsive()
    const doubleAccount = useDoubleContractAssetsAccountStore()

    const historicalTradeStore = useLoadMore<ApiResponse.HistoricalTrade>({
      isInitFetch: false,
      fetcher: ContractServices.getHistoricalTrade.bind(ContractServices),
      requestInterceptor: () => {
        return doubleAccount.openContractFeature === '1'
      },
      handleParams: (params) => {
        return { ...(params || {}), positionId: props.positionId, opType: '1' }
      },
    })

    const getOpenName = (symbolId: SymbolId) => {
      if (contractOrderListStore.userDoubleContractUnitType === UserDoubleContractUnitType.Coin) {
        return contractOrderListStore.getOpenName(symbolId)
      }
      return 'USDT'
    }

    onBeforeMount(() => {
      contractOrderListStore.userDoubleContractUnitType = window.localStorage.getItem(StorageIndexDBEnum.UserDoubleContractUnitType) || UserDoubleContractUnitType.USDT
    })

    watch(
      () => doubleAccount.doubleContractAssetsAccount?.openFutures,
      (openFutures) => {
        if (openFutures) {
          historicalTradeStore.initFetch()
        }
      },
      {
        immediate: true,
      },
    )

    return () => {
      if (responsive.isMobile) {
        return (
          <LoadMore loading={historicalTradeStore.loadMoreLoading.value} noMore={!historicalTradeStore.hasMore.value} onLoadMore={historicalTradeStore.loadMore}>
            <VirtualList itemMinHeight={264} data={historicalTradeStore.dataSource.value} noRenderWhenNotInView={false}>
              {{
                default: ({ data, index }: { data: ApiResponse.HistoricalTrade; index: number }) => {
                  return <HistoricalTradeItem data={data} divider={index !== historicalTradeStore.dataSource.value.length - 1} />
                },
              }}
            </VirtualList>
          </LoadMore>
        )
      }
      return (
        <OrderListLayout style={{ padding: 0 }}>
          <LoadMore loading={historicalTradeStore.loadMoreLoading.value} noMore={!historicalTradeStore.hasMore.value} onLoadMore={historicalTradeStore.loadMore}>
            <HocElTable data={historicalTradeStore.dataSource.value}>
              {{
                default: () => (
                  <>
                    <ElTableColumn label={t('币种')} minWidth={180}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          return (
                            <div class={'flex align-center gap-row-8'}>
                              <img width={32} src={baseInfoStore.getContractCoinIconUrlBySymbolId(row.symbolId)} alt="" />
                              <div class={'symbol-info'}>
                                <span class="symbol-name">
                                  {exchangeStore.getContractSymbolName(row.symbolId)} {t('永续')}
                                </span>
                                <div class="flex info-tags gap-4">
                                  <Tag color={row.direction === OrderDirection.Buy ? theme.value.colors.up : theme.value.colors.down}>{contractOrderListStore.getOrderType(row)}</Tag>
                                </div>
                              </div>
                            </div>
                          )
                        },
                      }}
                    </ElTableColumn>
                    <ElTableColumn prop="rlzPnl" label={t('已实现盈亏')} minWidth={160}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          return <Pnl value={row.pnl}>USDT</Pnl>
                        },
                      }}
                    </ElTableColumn>

                    <ElTableColumn prop="price" label={t('成交价格') + '(USDT)'} minWidth={160}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalCommission }) => {
                          return `${row.price.formatNumberWithCommas()}`
                        },
                      }}
                    </ElTableColumn>

                    <ElTableColumn prop="price" label={t('成交数量')} minWidth={160}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          const num = contractOrderListStore.getDealInterestTrade(row)
                          const amount =
                            contractOrderListStore.userDoubleContractUnitType === UserDoubleContractUnitType.Coin
                              ? num.formatNumberWithCommas()
                              : num.multiplyStr(row.price).formatNumberWithCommas()
                          return `${amount}` + ` ${getOpenName(row.symbolId)}`
                        },
                      }}
                    </ElTableColumn>

                    <ElTableColumn prop="fee" label={t('手续费') + '(USDT)'} minWidth={160}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          return `${row.fee.formatCommonMoney()}`
                        },
                      }}
                    </ElTableColumn>

                    <ElTableColumn prop="isMaker" label={t('角色')}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          return row.isMaker === -1 ? t('吃单') : t('挂单')
                        },
                      }}
                    </ElTableColumn>

                    <ElTableColumn prop="orderNo" label={t('订单号')} minWidth={260} showOverflowTooltip>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          return <CopyText value={row.orderNo || row.tid} isRenderCopyText />
                        },
                      }}
                    </ElTableColumn>

                    <ElTableColumn prop="updateTime" label={t('成交时间')} minWidth={180}>
                      {{
                        default: ({ row }: { row: ApiResponse.HistoricalTrade }) => {
                          return dayjs(row.updateTime).format('YYYY-MM-DD HH:mm:ss')
                        },
                      }}
                    </ElTableColumn>
                  </>
                ),
                empty: () => {
                  return (
                    <FullScreenMinHeight>
                      <EmptyData />
                    </FullScreenMinHeight>
                  )
                },
              }}
            </HocElTable>
          </LoadMore>
        </OrderListLayout>
      )
    }
  },
})
